/**
 * フレンド招待（T16）関連の定数と型定義。
 *
 * ハードコード禁止ルール（.claude/rules/no-hardcode.md）に従い、
 * URL パラメータキー / テキストテンプレート / ストレージキー / リトライ回数を
 * 本ファイルに集約する。
 *
 * 関連:
 *   - src/services/social.ts — 招待コード生成・受諾・共有
 *   - src/components/InviteFriendsModal.tsx — UI
 */

// ---------------------------------------------------------------------------
// URL / ストレージ
// ---------------------------------------------------------------------------

/**
 * 招待 URL のクエリパラメータキー。
 * LIFF の liff.state, hash, 通常の search すべてで同じキーを使う。
 * Hub 側の他ゲームとも合わせて `ymg_invite` に固定（Match3/Sweeper と同一）。
 */
export const INVITE_PARAM_KEY = 'ymg_invite'

/**
 * 未認証時に未処理の招待コードを一時保存するための localStorage キー。
 * 認証後に `processPendingInvite()` が再試行する。
 */
export const INVITE_STORAGE_KEY = 'luckyanimal_nawabari_pending_invite_v1'

/**
 * 既に受諾済みの招待コードを記録する localStorage キー（重複受諾防止）。
 * Hub 側は冪等に成功を返す仕様だが、クライアント側でも「既に処理済み」を
 * すぐ判別できるようキャッシュしておく。
 */
export const INVITE_CONSUMED_STORAGE_KEY = 'luckyanimal_nawabari_consumed_invites_v1'

/** キャッシュに残す受諾済みコードの最大件数（FIFO）。 */
export const INVITE_CONSUMED_MAX_ENTRIES = 32

// ---------------------------------------------------------------------------
// 共有テキスト
// ---------------------------------------------------------------------------

/**
 * 共有テキストのテンプレート。`{url}` が招待 URL に置換される。
 * 改行込み。Web Share API / LIFF / clipboard 共通。
 */
export const INVITE_SHARE_TEXT_TEMPLATE =
  'ラッキーどうぶつナワバリで一緒に遊ぼう!\n{url}'

/** Web Share API の title として使うタイトル文字列。 */
export const INVITE_SHARE_TITLE = 'ラッキーどうぶつナワバリ'

// ---------------------------------------------------------------------------
// リトライ
// ---------------------------------------------------------------------------

/**
 * `acceptFriendInvite()` の自動リトライ最大回数。
 * 429 / 5xx / ネットワーク障害時に指数バックオフで試行する。
 * 認証エラーなど「リトライ不可」なエラーは即 throw する。
 */
export const INVITE_ACCEPT_MAX_RETRY = 3

/** 初回リトライまでの待機時間（ms）。以降は 2 倍ずつ伸ばす。 */
export const INVITE_ACCEPT_BACKOFF_BASE_MS = 500

// ---------------------------------------------------------------------------
// UI ラベル
// ---------------------------------------------------------------------------

export const INVITE_UI = {
  modalTitle: 'ともだちを誘う',
  description:
    '招待リンクをシェアして、ともだちにこのゲームを知らせよう。リンクを開いた相手と自動的にフレンドになります。',
  descriptionSignedOut:
    'フレンド招待にはログインが必要です。ログイン後にもう一度お試しください。',
  shareButton: 'リンクをシェア',
  shareButtonBusy: 'リンクを作成中…',
  copyButton: 'コピー',
  copiedLabel: 'コピーしました',
  generatingLabel: 'リンクを作成中…',
  statusShared: '送信しました!',
  statusCopiedClipboard: 'クリップボードにコピーしました',
  statusSharedLine: 'LINE に送信しました',
  statusSharedWebShare: '共有しました',
  statusSharedCapacitor: '共有シートに送りました',
  statusCancelled: '共有をキャンセルしました',
  errorGenerate: '招待リンクの作成に失敗しました',
  errorShare: '共有に失敗しました',
  retryButton: '再試行',
  closeButton: '閉じる',
  signedOutActionLabel: 'ログイン方法をみる',
  entryLabel: 'ともだちを誘う',
  codeLabel: '招待コード',
  urlLabel: '招待 URL',
} as const

// ---------------------------------------------------------------------------
// 型定義
// ---------------------------------------------------------------------------

/**
 * 招待の共有方法。
 * - `liff`: LINE Mini App の `liff.shareTargetPicker()` 経由
 * - `web_share`: `navigator.share()` 経由（Android Chrome / iOS Safari）
 * - `capacitor_share`: Capacitor Share Plugin（現状未導入。将来 plugin を
 *   追加したときに自動で有効化される分岐枠）
 * - `clipboard`: 上記すべてが使えないブラウザでのフォールバック
 */
export type InviteShareMethod =
  | 'liff'
  | 'web_share'
  | 'capacitor_share'
  | 'clipboard'

export interface InviteShareResult {
  success: boolean
  method: InviteShareMethod
  /** エラーが発生した場合のメッセージ（ユーザー向け短文）。 */
  error?: string
}

/** Hub の `/games/:gameId/friends/invites` レスポンス。 */
export interface InviteCreateResponse {
  code: string
  url: string
  /** Unix epoch ms。未定義のケースもあり得るため optional。 */
  expiresAt?: number
}
